import { ChevronDown, ChevronRight, Loader2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ServicesQuery } from "@/gql/graphql";
import { useSalonStore } from "@/lib/store/salon";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/utils/pricing-utils";
import { useDiscountServices } from "../hooks/use-discount-services";

type AppliesToValue = "all" | "specific_services";
type ServiceItem = NonNullable<ServicesQuery["services"]>[number];

interface DiscountAppliesSectionProps {
  appliesTo: AppliesToValue;
  onAppliesToChange: (value: AppliesToValue) => void;
  selectedServiceIds: string[];
  onServiceIdsChange: (ids: string[]) => void;
  disabled?: boolean;
  /** Discount value type for price preview */
  discountValueType?: "percentage" | "fixed";
  /** Discount value (percentage 0-100 or fixed in cents) for price preview */
  discountValue?: number;
}

function getServicePrice(service: ServiceItem): {
  price: number | null;
  isRange: boolean;
  minPrice?: number;
  maxPrice?: number;
} {
  const display = service.displayPrice;
  if (!display) {
    return { price: service.basePrice ?? null, isRange: false };
  }

  if (display.mode === "SINGLE_PRICE" && display.fixedPrice != null) {
    return { price: display.fixedPrice, isRange: false };
  }

  if (display.mode === "MATRIX_PRICE" && display.priceRange) {
    return {
      price: null,
      isRange: true,
      minPrice: display.priceRange.minPrice,
      maxPrice: display.priceRange.maxPrice,
    };
  }

  if (display.mode === "NO_PRICE") {
    return { price: null, isRange: false };
  }

  return { price: service.basePrice ?? null, isRange: false };
}

function formatDiscountedPrice(
  price: number,
  valueType: "percentage" | "fixed",
  value: number,
  currency: string
): string {
  if (value <= 0) return formatPrice(price, currency);
  const savings = valueType === "percentage" ? Math.floor((price * value) / 100) : value;
  return formatPrice(Math.max(0, price - savings), currency);
}

export function DiscountAppliesSection({
  appliesTo,
  onAppliesToChange,
  selectedServiceIds,
  onServiceIdsChange,
  disabled = false,
  discountValueType,
  discountValue,
}: DiscountAppliesSectionProps) {
  const { t } = useTranslation("discounts");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const currency = useSalonStore((s) => s.currentSalon?.currency ?? "USD");

  const { services, categories, loading } = useDiscountServices();

  const bookableServices = services.filter((s) => s.allowOnlineBooking);

  const selectedIds: string[] = Array.isArray(selectedServiceIds)
    ? selectedServiceIds
    : selectedServiceIds
      ? Array.from(selectedServiceIds as Iterable<string>)
      : [];

  const setSelectedIds = (next: string[] | ((prev: string[]) => string[])) => {
    if (typeof next === "function") {
      onServiceIdsChange(next(selectedIds));
    } else {
      onServiceIdsChange(next);
    }
  };

  const handleToggleService = (id: string) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(selectedIds.filter((i) => i !== id));
    } else {
      setSelectedIds([...selectedIds, id]);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const toggleAllServicesInCategory = (categoryId: string) => {
    const categoryServices = bookableServices.filter((s) => s.category?.id === categoryId);
    const categoryServiceIds = categoryServices.map((s) => s.id);
    const allSelected = categoryServiceIds.every((id) => selectedIds.includes(id));

    if (allSelected) {
      setSelectedIds(selectedIds.filter((id) => !categoryServiceIds.includes(id)));
    } else {
      setSelectedIds((prev) => {
        const newIds = [...prev];
        categoryServiceIds.forEach((id) => {
          if (!newIds.includes(id)) newIds.push(id);
        });
        return newIds;
      });
    }
  };

  const hasDiscount = discountValueType && discountValue && discountValue > 0;

  const renderServicePrice = (service: ServiceItem) => {
    const priceInfo = getServicePrice(service);

    if (priceInfo.isRange && priceInfo.minPrice != null && priceInfo.maxPrice != null) {
      if (hasDiscount) {
        return (
          <div className="text-right flex-shrink-0">
            <div className="text-xs font-medium text-emerald-600">
              {formatDiscountedPrice(
                priceInfo.minPrice,
                discountValueType,
                discountValue,
                currency
              )}
              {" - "}
              {formatDiscountedPrice(
                priceInfo.maxPrice,
                discountValueType,
                discountValue,
                currency
              )}
            </div>
            <div className="text-[10px] text-muted-foreground line-through">
              {formatPrice(priceInfo.minPrice, currency)} -{" "}
              {formatPrice(priceInfo.maxPrice, currency)}
            </div>
          </div>
        );
      }
      return (
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {formatPrice(priceInfo.minPrice, currency)} - {formatPrice(priceInfo.maxPrice, currency)}
        </span>
      );
    }

    if (priceInfo.price == null) return null;

    if (hasDiscount) {
      return (
        <div className="text-right flex-shrink-0">
          <div className="text-xs font-medium text-emerald-600">
            {formatDiscountedPrice(priceInfo.price, discountValueType, discountValue, currency)}
          </div>
          <div className="text-[10px] text-muted-foreground line-through">
            {formatPrice(priceInfo.price, currency)}
          </div>
        </div>
      );
    }

    return (
      <span className="text-xs text-muted-foreground flex-shrink-0">
        {formatPrice(priceInfo.price, currency)}
      </span>
    );
  };

  const renderServiceOriginalPrice = (service: ServiceItem) => {
    const priceInfo = getServicePrice(service);

    if (priceInfo.isRange && priceInfo.minPrice != null && priceInfo.maxPrice != null) {
      return (
        <span className="text-xs text-muted-foreground flex-shrink-0">
          {formatPrice(priceInfo.minPrice, currency)} - {formatPrice(priceInfo.maxPrice, currency)}
        </span>
      );
    }

    if (priceInfo.price == null) return null;

    return (
      <span className="text-xs text-muted-foreground flex-shrink-0">
        {formatPrice(priceInfo.price, currency)}
      </span>
    );
  };

  const renderServiceCard = (service: ServiceItem) => {
    const isSelected = selectedIds.includes(service.id);
    return (
      <button
        key={service.id}
        type="button"
        onClick={() => !disabled && handleToggleService(service.id)}
        disabled={disabled}
        className={cn(
          "flex items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition-colors border w-full",
          isSelected
            ? "bg-primary/10 border-primary/30 text-primary"
            : "bg-card border-border hover:bg-accent"
        )}
      >
        <Checkbox
          checked={isSelected}
          className="pointer-events-none flex-shrink-0"
          disabled={disabled}
        />
        <span className="truncate flex-1">{service.name}</span>
        {isSelected ? renderServicePrice(service) : renderServiceOriginalPrice(service)}
      </button>
    );
  };

  return (
    <div className="space-y-3">
      <label className="text-sm font-medium">{t("form.appliesTo.label")}</label>

      <div className="w-full">
        <Select
          value={appliesTo}
          onValueChange={(value) => {
            onAppliesToChange(value as AppliesToValue);
            onServiceIdsChange([]);
          }}
          disabled={disabled}
        >
          <SelectTrigger>
            <SelectValue>
              {appliesTo === "all" ? t("form.appliesTo.all") : t("form.appliesTo.specificServices")}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t("form.appliesTo.all")}</SelectItem>
            <SelectItem value="specific_services">
              {t("form.appliesTo.specificServices")}
            </SelectItem>
          </SelectContent>
        </Select>
      </div>

      {appliesTo !== "all" && (
        <div className="space-y-2">
          {bookableServices.length > 0 ? (
            <>
              {categories.map((category) => {
                const categoryServices = bookableServices.filter(
                  (s) => s.category?.id === category.id
                );
                if (categoryServices.length === 0) return null;
                const categoryServiceIds = categoryServices.map((s) => s.id);
                const allSelected = categoryServiceIds.every((id) => selectedIds.includes(id));
                const someSelected = categoryServiceIds.some((id) => selectedIds.includes(id));
                const isExpanded = expandedCategories.has(category.id);
                const selectedCount = categoryServiceIds.filter((id) =>
                  selectedIds.includes(id)
                ).length;

                return (
                  <div key={category.id} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        checked={allSelected ? true : someSelected ? "indeterminate" : false}
                        onCheckedChange={() => toggleAllServicesInCategory(category.id)}
                        onClick={(e) => e.stopPropagation()}
                        disabled={disabled}
                      />
                      <button
                        type="button"
                        onClick={() => toggleCategory(category.id)}
                        className="flex items-center gap-1.5 flex-1"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {category.name}
                        </span>
                        {selectedCount > 0 && (
                          <span className="text-xs text-primary font-medium">
                            ({selectedCount})
                          </span>
                        )}
                      </button>
                    </div>
                    {isExpanded && (
                      <div className="space-y-1 pl-6">
                        {categoryServices.map(renderServiceCard)}
                      </div>
                    )}
                  </div>
                );
              })}

              {bookableServices.some((s) => !s.category?.id) &&
                (() => {
                  const uncategorizedServices = bookableServices.filter((s) => !s.category?.id);
                  const isExpanded = expandedCategories.has("uncategorized");

                  return (
                    <div className="space-y-1">
                      <button
                        type="button"
                        onClick={() => toggleCategory("uncategorized")}
                        className="flex items-center gap-1.5 pl-6"
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                        <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                          {t("form.appliesTo.uncategorized")}
                        </span>
                      </button>
                      {isExpanded && (
                        <div className="space-y-1 pl-6">
                          {uncategorizedServices.map(renderServiceCard)}
                        </div>
                      )}
                    </div>
                  );
                })()}
            </>
          ) : loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="text-center py-4 text-muted-foreground text-sm">
              {t("form.appliesTo.noResults")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
